import { createClient } from '@supabase/supabase-js';

/* --------------------------- ENV + BASIC CLIENT --------------------------- */

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase env: VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
  db: { schema: 'public' },
});

/* ------------------------------- UTILITIES -------------------------------- */

const withTimeout = async (promise, ms = 8000) => {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), ms);
  try {
    const res = await promise(ctrl.signal);
    return res;
  } finally {
    clearTimeout(timer);
  }
};

/* ---------------------------- HEALTH CHECKS ------------------------------- */
/**
 * Auth health: hits the real Auth health endpoint with a short timeout.
 * This is the most accurate way to detect reachability of Supabase Auth.
 */
export const testAuthService = async () => {
  try {
    const url = `${supabaseUrl.replace(/\/$/, '')}/auth/v1/health`;
    const res = await withTimeout(
      (signal) =>
        fetch(url, {
          method: 'GET',
          headers: { apikey: supabaseAnonKey },
          signal,
        }),
      6000
    );
    const ok = res.ok; // 200 -> "OK"
    if (!ok) {
      console.warn('Auth health non-200:', res.status);
    }
    return ok;
  } catch (err) {
    console.error('Auth health fetch failed:', err?.name || err?.message);
    return false;
  }
};

/**
 * DB connectivity check: very light HEAD-like select against a tiny table.
 * If you changed your seed tables, swap "orgs" to a guaranteed public table.
 */
export const testDatabaseConnection = async () => {
  try {
    const { error } = await supabase
      .from('orgs')
      .select('id', { count: 'exact', head: true })
      .limit(0);

    if (error) {
      console.warn('DB check error:', error.message || error);
      return false;
    }
    return true;
  } catch (err) {
    console.error('DB check failed:', err?.message);
    return false;
  }
};

/**
 * Enhanced auth test:
 * 1) ping /auth/v1/health with timeout
 * 2) then do a cheap auth op (getUser) to ensure SDK wiring is OK
 *    NOTE: getUser() may return an auth error when logged out, which is fine.
 */
export const testAuthServiceEnhanced = async () => {
  const healthy = await testAuthService();
  if (!healthy) {
    return { healthy: false, error: 'Auth health endpoint not reachable' };
  }

  try {
    const { data, error } = await supabase.auth.getUser();
    // If logged out, Supabase returns an error — that still proves reachability.
    if (error && error.status >= 500) {
      return { healthy: false, error: error.message || 'Auth server error' };
    }
    return { healthy: true, status: 200, userPresent: !!data?.user };
  } catch (err) {
    return { healthy: false, error: err?.message || 'Auth SDK call failed' };
  }
};

/**
 * Combined test (optional): returns true only if both services are reachable.
 */
export const testSupabaseConnection = async () => {
  const [authOk, dbOk] = await Promise.all([
    testAuthService(),
    testDatabaseConnection(),
  ]);
  return authOk && dbOk;
};
